import React from "react";
import {
  Box,
  Typography,
  Rating,
} from "@mui/material";
import { Star } from "@mui/icons-material";
import GppGoodIcon from '@mui/icons-material/GppGood';
import PaidIcon from '@mui/icons-material/Paid';

// ==============================================
// CONFIGURACIONES Y CONSTANTES GLOBALES (SOLO LEGACY)
// ==============================================

const SQUIRCLE_CONFIG = {
  SMOOTHNESS: 0.6,
  RESOLUTION: 128,
};

// ==============================================
// COMPONENTES INTERNOS (AUTOCONTENIDOS) - SOLO PARA LEGACY
// ==============================================

const EstrellasPuntuacion = ({ puntuacion, centered = false }) => {
  return (
    <Box display="flex" justifyContent={centered ? "center" : "flex-start"}>
      <Rating
        value={puntuacion}
        readOnly
        precision={0.1}
        emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />}
      />
    </Box>
  );
};

const RatingDisplay = ({ evaluaciones, centered = false }) => {
  if (!evaluaciones) return null;

  const rating = parseFloat(evaluaciones.promedio);
  const totalReviews = evaluaciones.cantidad;

  const handleClick = () => {
    const evaluacionesSection = document.getElementById("evaluaciones-section");
    if (evaluacionesSection) {
      evaluacionesSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const ratingContent = (
    <Box
      onClick={handleClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        '&:hover': { opacity: 0.8 },
        justifyContent: centered ? 'center' : 'flex-start',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Rating
          value={rating}
          readOnly
          precision={0.1}
          size="small"
          emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />}
        />
      </Box>
      <Typography
        component="span"
        sx={{
          fontFamily: 'Quicksand-Bold',
          fontSize: '16px',
          color: 'text.secondary',
        }}
      >
        {evaluaciones.promedio}
      </Typography>
      <Typography
        component="span"
        sx={{
          color: 'text.secondary',
          fontSize: '16px',
          textDecoration: 'underline',
          fontFamily: 'Quicksand-Regular',
        }}
      >
        ({totalReviews} evaluaciones)
      </Typography>
    </Box>
  );

  return ratingContent;
};

// ==============================================
// COMPONENTE LEGACY (PRESERVADO EXACTO)
// ==============================================

const ProfessionalInfo = ({ kinesiologoInfo, isMobile, shouldShowEvaluaciones }) => {
  if (!kinesiologoInfo || !kinesiologoInfo.perfil) {
    return <Typography> </Typography>;
  }

  const { profesional_nombre_plataforma, profesional_profesion } = kinesiologoInfo.perfil;

  if (isMobile) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontFamily: "Quicksand-Bold", mb: 0 }}>
          {profesional_nombre_plataforma}
        </Typography>
        <Typography sx={{ fontFamily: "Quicksand-Regular", mb: 0 }}>
          {profesional_profesion}
        </Typography>
        {shouldShowEvaluaciones && (
          <Box sx={{ mt: 0, mb: 0 }}>
            <RatingDisplay evaluaciones={kinesiologoInfo.evaluaciones} />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ ml: 2, fontFamily: "Quicksand-Bold", mb: 0 }}>
        {profesional_nombre_plataforma}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", ml: 2, mb: 0 }}>
        <Typography sx={{ fontFamily: "Quicksand-Regular", mb: 0 }}>
          {profesional_profesion}
        </Typography>
        {shouldShowEvaluaciones && (
          <Box sx={{ ml: 0.5, mb: 0 }}>
            <RatingDisplay evaluaciones={kinesiologoInfo.evaluaciones} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ==============================================
// LÓGICA DE DETERMINACIÓN DE ESCENARIOS
// ==============================================

const determineScenario = (profesionalInfo, isMobile) => {
  // Validación de datos
  if (!profesionalInfo || !profesionalInfo.perfil) {
    return isMobile ? 'legacyMobile' : 'legacyDesktop';
  }

  const hasProfilePhoto = profesionalInfo.perfil.profesional_foto_perfil;
  const hasCoverPhoto = profesionalInfo.perfil.profesional_foto_portada;

  // Si no hay foto de perfil, usar legacy (regla de negocio clave)
  if (!hasProfilePhoto) {
    return isMobile ? 'legacyMobile' : 'legacyDesktop';
  }

  // Determinar escenario con nuevo sistema
  if (isMobile) {
    return hasCoverPhoto ? 'newMobileProfileCover' : 'newMobileProfileOnly';
  } else {
    return hasCoverPhoto ? 'newDesktopProfileCover' : 'newDesktopProfileOnly';
  }
};

// ==============================================
// FUNCIONES DE RENDERIZADO INDEPENDIENTES
// ==============================================

const renderNewMobileProfileCover = (profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones) => {
  // CONSTANTES ESPECÍFICAS PARA ESTA VISTA
  const PROFILE_SIZE_MOBILE = 187; // px
  const INVASION_CONFIG_MOBILE = {
    ABOVE_PERCENTAGE: 0.20, // 60% arriba
    BELOW_PERCENTAGE: 0.80, // 40% abajo
  };
  const SQUIRCLE_CONFIG_LOCAL = {
    SMOOTHNESS: 0.6,
    RESOLUTION: 128,
  };

  // FUNCIÓN SQUIRCLE AUTOCONTENIDA
  const generateSquircleClipPath = (size = 300, smoothness = 0.6, resolution = 128) => {
    const points = [];
    const radius = size / 2;
    const step = (2 * Math.PI) / resolution;
    const n = 2 + (18 * (1 - smoothness)); // Convertir smoothness a exponente
    
    for (let i = 0; i <= resolution; i++) {
      const angle = i * step;
      const cosTheta = Math.cos(angle);
      const sinTheta = Math.sin(angle);
      
      // Fórmula del superellipse en coordenadas paramétricas
      const x = Math.sign(cosTheta) * Math.pow(Math.abs(cosTheta), 2/n) * radius;
      const y = Math.sign(sinTheta) * Math.pow(Math.abs(sinTheta), 2/n) * radius;
      
      // Convertir a coordenadas del viewport (centrado)
      const viewportX = (x + radius) / size * 100;
      const viewportY = (y + radius) / size * 100;
      
      points.push(`${viewportX}% ${viewportY}%`);
    }
    
    return `polygon(${points.join(', ')})`;
  };

  // COMPONENTE RATING AUTOCONTENIDO
  const RatingDisplayLocal = ({ evaluaciones, centered = false }) => {
    if (!evaluaciones) return null;

    const rating = parseFloat(evaluaciones.promedio);
    const totalReviews = evaluaciones.cantidad;

    const handleClick = () => {
      const evaluacionesSection = document.getElementById("evaluaciones-section");
      if (evaluacionesSection) {
        evaluacionesSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const ratingContent = (
      <Box
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
          justifyContent: centered ? 'center' : 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Rating
            value={rating}
            readOnly
            precision={0.1}
            size="small"
            emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />}
          />
        </Box>
        <Typography
          component="span"
          sx={{
            fontFamily: 'Quicksand-Bold',
            fontSize: '16px',
            color: 'text.secondary',
          }}
        >
          {evaluaciones.promedio}
        </Typography>
        <Typography
          component="span"
          sx={{
            color: 'text.secondary',
            fontSize: '16px',
            textDecoration: 'underline',
            fontFamily: 'Quicksand-Regular',
          }}
        >
          ({totalReviews} evaluaciones)
        </Typography>
      </Box>
    );

    return ratingContent;
  };

  const { profesional_nombre_plataforma, profesional_profesion, profesional_foto_perfil, profesional_foto_portada } = profesionalInfo.perfil;

  return (
    <>
      {/* PORTADA CON AVATAR SUPERPUESTO */}
      <Box
        sx={{
          position: 'relative',
          width: '95%',
          margin: 'auto',
          marginTop: 0,
          marginBottom: 1,
        }}
      >
        {/* Portada con lógica de borde igual a foto de perfil */}
        <Box
          sx={{
            width: 'calc(100% - 15px)',
            margin: 'auto',
            padding: '7.5px',
            background: 'white',
            borderRadius: 2.5,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={profesional_foto_portada}
              alt="Portada profesional"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
              }}
            />
          </Box>
        </Box>

        {/* Avatar superpuesto CON BORDE */}
        <Box
          sx={{
            position: 'absolute',
            top: `calc(100% - ${INVASION_CONFIG_MOBILE.ABOVE_PERCENTAGE * PROFILE_SIZE_MOBILE}px)`,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            width: `${PROFILE_SIZE_MOBILE + 10}px`,
            height: `${PROFILE_SIZE_MOBILE + 10}px`,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              padding: '5px',
              background: 'white',
              clipPath: generateSquircleClipPath(PROFILE_SIZE_MOBILE + 10, SQUIRCLE_CONFIG_LOCAL.SMOOTHNESS, SQUIRCLE_CONFIG_LOCAL.RESOLUTION),
            }}
          >
            <img
              src={profesional_foto_perfil}
              alt={profesional_nombre_plataforma || "Profesional"}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                clipPath: generateSquircleClipPath(PROFILE_SIZE_MOBILE, SQUIRCLE_CONFIG_LOCAL.SMOOTHNESS, SQUIRCLE_CONFIG_LOCAL.RESOLUTION),
              }}
            />
          </Box>
        </Box>
      </Box>
      
      {/* TEXTOS DEBAJO DEL AVATAR */}
      <Box sx={{ marginTop: `${INVASION_CONFIG_MOBILE.BELOW_PERCENTAGE * PROFILE_SIZE_MOBILE + 16}px` }}>
        <Typography variant="h5" sx={{ fontFamily: "Quicksand-Bold", mb: 0, textAlign: 'left' }}>
          {profesional_nombre_plataforma}
        </Typography>
        <Typography sx={{ fontFamily: "Quicksand-Regular", mb: 0, textAlign: 'left' }}>
          {profesional_profesion}
        </Typography>
        {shouldShowEvaluaciones && (
          <Box sx={{ mt: 0, mb: 0, display: 'flex', justifyContent: 'flex-start' }}>
            <RatingDisplayLocal evaluaciones={profesionalInfo.evaluaciones} centered={false} />
          </Box>
        )}
      </Box>

      {/* CERTIFICACIONES */}
      {shouldShowCertifications && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 0.25,
              mt: 0.25,
            }}
          >
            <GppGoodIcon sx={{ fontSize: "18px", color: "#3B82F6", mr: 0.625 }} />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                textAlign: "left",
                fontSize: "13px",
                mb: 0,
              }}
            >
              Certificado por Superintendencia de Salud
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 0,
            }}
          >
            <PaidIcon sx={{ fontSize: "18px", color: "#3B82F6", mr: 0.625 }} />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                textAlign: "left",
                mb: 0,
                fontSize: "13px",
              }}
            >
              Reembolsable por ISAPRE y Seguros
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

const renderNewMobileProfileOnly = (profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones) => {
  // CONSTANTES ESPECÍFICAS PARA ESTA VISTA
  const PROFILE_SIZE_MOBILE = 187; // px (50% más grande que los 125px anteriores)
  const SQUIRCLE_CONFIG_LOCAL = {
    SMOOTHNESS: 0.6,
    RESOLUTION: 128,
  };

  // FUNCIÓN SQUIRCLE AUTOCONTENIDA
  const generateSquircleClipPath = (size = 300, smoothness = 0.6, resolution = 128) => {
    const points = [];
    const radius = size / 2;
    const step = (2 * Math.PI) / resolution;
    const n = 2 + (18 * (1 - smoothness)); // Convertir smoothness a exponente
    
    for (let i = 0; i <= resolution; i++) {
      const angle = i * step;
      const cosTheta = Math.cos(angle);
      const sinTheta = Math.sin(angle);
      
      // Fórmula del superellipse en coordenadas paramétricas
      const x = Math.sign(cosTheta) * Math.pow(Math.abs(cosTheta), 2/n) * radius;
      const y = Math.sign(sinTheta) * Math.pow(Math.abs(sinTheta), 2/n) * radius;
      
      // Convertir a coordenadas del viewport (centrado)
      const viewportX = (x + radius) / size * 100;
      const viewportY = (y + radius) / size * 100;
      
      points.push(`${viewportX}% ${viewportY}%`);
    }
    
    return `polygon(${points.join(', ')})`;
  };

  // COMPONENTE RATING AUTOCONTENIDO
  const RatingDisplayLocal = ({ evaluaciones, centered = false }) => {
    if (!evaluaciones) return null;

    const rating = parseFloat(evaluaciones.promedio);
    const totalReviews = evaluaciones.cantidad;

    const handleClick = () => {
      const evaluacionesSection = document.getElementById("evaluaciones-section");
      if (evaluacionesSection) {
        evaluacionesSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const ratingContent = (
      <Box
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
          justifyContent: centered ? 'center' : 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Rating
            value={rating}
            readOnly
            precision={0.1}
            size="small"
            emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />}
          />
        </Box>
        <Typography
          component="span"
          sx={{
            fontFamily: 'Quicksand-Bold',
            fontSize: '16px',
            color: 'text.secondary',
          }}
        >
          {evaluaciones.promedio}
        </Typography>
        <Typography
          component="span"
          sx={{
            color: 'text.secondary',
            fontSize: '16px',
            textDecoration: 'underline',
            fontFamily: 'Quicksand-Regular',
          }}
        >
          ({totalReviews} evaluaciones)
        </Typography>
      </Box>
    );

    return ratingContent;
  };

  const { profesional_nombre_plataforma, profesional_profesion, profesional_foto_perfil } = profesionalInfo.perfil;

  return (
    <>
      {/* AVATAR CENTRADO CON BORDE */}
      <Box
        sx={{
          position: 'relative',
          width: `${PROFILE_SIZE_MOBILE + 10}px`,
          height: `${PROFILE_SIZE_MOBILE + 10}px`,
          margin: 'auto',
          marginTop: 0,
          marginBottom: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            padding: '5px',
            background: 'white',
            clipPath: generateSquircleClipPath(PROFILE_SIZE_MOBILE + 10, SQUIRCLE_CONFIG_LOCAL.SMOOTHNESS, SQUIRCLE_CONFIG_LOCAL.RESOLUTION),
          }}
        >
          <img
            src={profesional_foto_perfil}
            alt={profesional_nombre_plataforma || "Profesional"}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              clipPath: generateSquircleClipPath(PROFILE_SIZE_MOBILE, SQUIRCLE_CONFIG_LOCAL.SMOOTHNESS, SQUIRCLE_CONFIG_LOCAL.RESOLUTION),
            }}
          />
        </Box>
      </Box>

      {/* TEXTOS ALINEADOS A LA IZQUIERDA DEBAJO */}
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="h5" sx={{ fontFamily: "Quicksand-Bold", mb: 0 }}>
          {profesional_nombre_plataforma}
        </Typography>
        <Typography sx={{ fontFamily: "Quicksand-Regular", mb: 0 }}>
          {profesional_profesion}
        </Typography>
        {shouldShowEvaluaciones && (
          <Box sx={{ mt: 0, mb: 0, display: 'flex', justifyContent: 'flex-start' }}>
            <RatingDisplayLocal evaluaciones={profesionalInfo.evaluaciones} centered={false} />
          </Box>
        )}
      </Box>

      {/* CERTIFICACIONES ALINEADAS A LA IZQUIERDA */}
      {shouldShowCertifications && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 0.25,
              mt: 0.25,
            }}
          >
            <GppGoodIcon sx={{ fontSize: "18px", color: "#3B82F6", mr: 0.625 }} />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                textAlign: "left",
                fontSize: "13px",
                mb: 0,
              }}
            >
              Certificado por Superintendencia de Salud
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 0,
            }}
          >
            <PaidIcon sx={{ fontSize: "18px", color: "#3B82F6", mr: 0.625 }} />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                textAlign: "left",
                mb: 0,
                fontSize: "13px",
              }}
            >
              Reembolsable por ISAPRE y Seguros
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

const renderNewDesktopProfileCover = (profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones) => {
  // CONSTANTES ESPECÍFICAS PARA ESTA VISTA
  const PROFILE_SIZE_DESKTOP = 250; // px
  const INVASION_CONFIG_DESKTOP = {
    ABOVE_PERCENTAGE: 0.28, // 60% arriba
    BELOW_PERCENTAGE: 0.72, // 40% abajo
  };
  const SQUIRCLE_CONFIG_LOCAL = {
    SMOOTHNESS: 0.6,
    RESOLUTION: 128,
  };

  // FUNCIÓN SQUIRCLE AUTOCONTENIDA
  const generateSquircleClipPath = (size = 300, smoothness = 0.6, resolution = 128) => {
    const points = [];
    const radius = size / 2;
    const step = (2 * Math.PI) / resolution;
    const n = 2 + (18 * (1 - smoothness)); // Convertir smoothness a exponente
    
    for (let i = 0; i <= resolution; i++) {
      const angle = i * step;
      const cosTheta = Math.cos(angle);
      const sinTheta = Math.sin(angle);
      
      // Fórmula del superellipse en coordenadas paramétricas
      const x = Math.sign(cosTheta) * Math.pow(Math.abs(cosTheta), 2/n) * radius;
      const y = Math.sign(sinTheta) * Math.pow(Math.abs(sinTheta), 2/n) * radius;
      
      // Convertir a coordenadas del viewport (centrado)
      const viewportX = (x + radius) / size * 100;
      const viewportY = (y + radius) / size * 100;
      
      points.push(`${viewportX}% ${viewportY}%`);
    }
    
    return `polygon(${points.join(', ')})`;
  };

  // COMPONENTE RATING AUTOCONTENIDO
  const RatingDisplayLocal = ({ evaluaciones, centered = false }) => {
    if (!evaluaciones) return null;

    const rating = parseFloat(evaluaciones.promedio);
    const totalReviews = evaluaciones.cantidad;

    const handleClick = () => {
      const evaluacionesSection = document.getElementById("evaluaciones-section");
      if (evaluacionesSection) {
        evaluacionesSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const ratingContent = (
        <Box
          onClick={handleClick}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
            justifyContent: centered ? 'center' : 'flex-start',
            mb: 0,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: 'Quicksand-Bold',
              fontSize: '26px',
              color: 'text.primary',
            }}
          >
            {evaluaciones.promedio}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Rating
              value={rating}
              readOnly
              precision={0.1}
              size="small"
              emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />}
            />
          </Box>
          <Typography
            component="span"
            sx={{
              color: 'text.primary',
              fontSize: '16px',
              textDecoration: 'underline',
              fontFamily: 'Quicksand-Regular',
            }}
          >
            ({totalReviews} evaluaciones)
          </Typography>
        </Box>
      );  

    return ratingContent;
  };

  const { profesional_nombre_plataforma, profesional_profesion, profesional_foto_perfil, profesional_foto_portada } = profesionalInfo.perfil;

  return (
    <>
      {/* PORTADA COMPLETA CON LÓGICA DE BORDE IGUAL A FOTO DE PERFIL */}
      <Box
        sx={{
          width: 'calc(100% - 20px)',
          margin: 'auto',
          padding: '10px',
          background: 'white',
          borderRadius: 2.5,
          marginBottom: `calc(-${INVASION_CONFIG_DESKTOP.ABOVE_PERCENTAGE * PROFILE_SIZE_DESKTOP}px)`,
          zIndex: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={profesional_foto_portada}
            alt="Portada profesional"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />
        </Box>
      </Box>

      {/* CONTENEDOR AVATAR + TEXTOS */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          width: '95%',
          margin: 'auto',
          position: 'relative',
          zIndex: 2,
          mb: 4,
        }}
      >
        {/* AVATAR COLUMNA CON BORDE */}
        <Box
          sx={{
            width: `${PROFILE_SIZE_DESKTOP + 10}px`,
            height: `${PROFILE_SIZE_DESKTOP + 10}px`,
            flexShrink: 0,
            marginRight: 2,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              padding: '5px',
              background: 'white',
              clipPath: generateSquircleClipPath(PROFILE_SIZE_DESKTOP + 10, SQUIRCLE_CONFIG_LOCAL.SMOOTHNESS, SQUIRCLE_CONFIG_LOCAL.RESOLUTION),
            }}
          >
            <img
              src={profesional_foto_perfil}
              alt={profesional_nombre_plataforma || "Profesional"}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                clipPath: generateSquircleClipPath(PROFILE_SIZE_DESKTOP, SQUIRCLE_CONFIG_LOCAL.SMOOTHNESS, SQUIRCLE_CONFIG_LOCAL.RESOLUTION),
              }}
            />
          </Box>
        </Box>

        {/* TEXTOS COLUMNA */}
        <Box sx={{ flex: 1, alignSelf: 'flex-end' }}>
          <Typography variant="h5" sx={{ fontFamily: "Quicksand-Bold", mb: 0.5, mt: 0 }}>
            {profesional_nombre_plataforma}
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand-Regular", mb: 0, mt: 0 }}>
            {profesional_profesion}
          </Typography>
          {/* EVALUACIONES DEBAJO DE LA PROFESIÓN */}
          {shouldShowEvaluaciones && (
            <Box sx={{ mb: 0.5 }}>
              <RatingDisplayLocal evaluaciones={profesionalInfo.evaluaciones} centered={false} />
            </Box>
          )}

          {/* CERTIFICACIONES */}
          {shouldShowCertifications && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  mb: 0.5,
                  mt: 0,
                }}
              >
                <GppGoodIcon sx={{ fontSize: "22px", color: "#3B82F6", mr: 0.625 }} />
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: "14px",
                    textAlign: "left",
                    mb: 0,
                  }}
                >
                  Profesional certificado por Superintendencia de Salud
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  mb: 0,
                }}
              >
                <PaidIcon sx={{ fontSize: "22px", color: "#3B82F6", mr: 0.625 }} />
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: "14px",
                    mb: 0,
                  }}
                >
                  Atenciones reembolsables por ISAPRE y Seguros
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

const renderNewDesktopProfileOnly = (profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones) => {
  // CONSTANTES ESPECÍFICAS PARA ESTA VISTA
  const PROFILE_SIZE_DESKTOP = 250; // px
  const SQUIRCLE_CONFIG_LOCAL = {
    SMOOTHNESS: 0.6,
    RESOLUTION: 128,
  };

  // FUNCIÓN SQUIRCLE AUTOCONTENIDA
  const generateSquircleClipPath = (size = 300, smoothness = 0.6, resolution = 128) => {
    const points = [];
    const radius = size / 2;
    const step = (2 * Math.PI) / resolution;
    const n = 2 + (18 * (1 - smoothness)); // Convertir smoothness a exponente
    
    for (let i = 0; i <= resolution; i++) {
      const angle = i * step;
      const cosTheta = Math.cos(angle);
      const sinTheta = Math.sin(angle);
      
      // Fórmula del superellipse en coordenadas paramétricas
      const x = Math.sign(cosTheta) * Math.pow(Math.abs(cosTheta), 2/n) * radius;
      const y = Math.sign(sinTheta) * Math.pow(Math.abs(sinTheta), 2/n) * radius;
      
      // Convertir a coordenadas del viewport (centrado)
      const viewportX = (x + radius) / size * 100;
      const viewportY = (y + radius) / size * 100;
      
      points.push(`${viewportX}% ${viewportY}%`);
    }
    
    return `polygon(${points.join(', ')})`;
  };

  // COMPONENTE RATING AUTOCONTENIDO
  const RatingDisplayLocal = ({ evaluaciones, centered = false }) => {
    if (!evaluaciones) return null;

    const rating = parseFloat(evaluaciones.promedio);
    const totalReviews = evaluaciones.cantidad;

    const handleClick = () => {
      const evaluacionesSection = document.getElementById("evaluaciones-section");
      if (evaluacionesSection) {
        evaluacionesSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const ratingContent = (
      <Box
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
          justifyContent: centered ? 'center' : 'flex-start',
          mb: 0,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: 'Quicksand-Bold',
            fontSize: '26px',
            color: 'text.primary',
          }}
        >
          {evaluaciones.promedio}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Rating
            value={rating}
            readOnly
            precision={0.1}
            size="small"
            emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />}
          />
        </Box>
        <Typography
          component="span"
          sx={{
            color: 'text.primary',
            fontSize: '16px',
            textDecoration: 'underline',
            fontFamily: 'Quicksand-Regular',
          }}
        >
          ({totalReviews} evaluaciones)
        </Typography>
      </Box>
    );

    return ratingContent;
  };

  const { profesional_nombre_plataforma, profesional_profesion, profesional_foto_perfil } = profesionalInfo.perfil;

  return (
    <>
      {/* CONTENEDOR AVATAR + TEXTOS */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          width: '100%',
          margin: 'auto',
          gap: 2,
          mb:4
        }}
      >
        {/* AVATAR COLUMNA CON BORDE */}
        <Box
          sx={{
            width: `${PROFILE_SIZE_DESKTOP + 10}px`,
            height: `${PROFILE_SIZE_DESKTOP + 10}px`,
            flexShrink: 0,
            mr: 2
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              padding: '5px',
              background: 'white',
              clipPath: generateSquircleClipPath(PROFILE_SIZE_DESKTOP + 10, SQUIRCLE_CONFIG_LOCAL.SMOOTHNESS, SQUIRCLE_CONFIG_LOCAL.RESOLUTION),
            }}
          >
            <img
              src={profesional_foto_perfil}
              alt={profesional_nombre_plataforma || "Profesional"}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                clipPath: generateSquircleClipPath(PROFILE_SIZE_DESKTOP, SQUIRCLE_CONFIG_LOCAL.SMOOTHNESS, SQUIRCLE_CONFIG_LOCAL.RESOLUTION),
              }}
            />
          </Box>
        </Box>

        {/* TEXTOS COLUMNA - Ajustado para alineación izquierda */}
        <Box sx={{ 
          flex: 1, 
          alignSelf: 'flex-end',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start', // Alinea todo a la izquierda
          width: '100%'
        }}>
          {/* Nombre */}
          <Typography variant="h5" sx={{ 
            fontFamily: "Quicksand-Bold", 
            mb: 0.5, 
            width: '100%' 
          }}>
            {profesional_nombre_plataforma}
          </Typography>
          
          {/* Profesión */}
          <Typography sx={{ 
            fontFamily: "Quicksand-Regular", 
            mb: 0,
            width: '100%'
          }}>
            {profesional_profesion}
          </Typography>

          {/* EVALUACIONES DEBAJO DE LA PROFESIÓN */}
          {shouldShowEvaluaciones && (
            <Box sx={{ 
              mb: 0.5,
              width: '100%'
            }}>
              <RatingDisplayLocal evaluaciones={profesionalInfo.evaluaciones} centered={false} />
            </Box>
          )}

          {/* CERTIFICACIONES */}
          {shouldShowCertifications && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 0.5,
                  width: '100%'
                }}
              >
                <GppGoodIcon sx={{ fontSize: "22px", color: "#3B82F6", mr: 0.625 }} />
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: "14px",
                    mb: 0.5,
                  }}
                >
                  Profesional certificado por Superintendencia de Salud
                </Typography>
              </Box>
              
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 0,
                  width: '100%'
                }}
              >
                <PaidIcon sx={{ fontSize: "22px", color: "#3B82F6", mr: 0.625 }} />
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: "14px",
                    mb: 0,
                  }}
                >
                  Atenciones reembolsables por ISAPRE y Seguros
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

const renderLegacyMobile = (profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones) => {
  return (
    <>
      {/* Imagen del profesional */}
      <Box
        sx={{
          position: 'relative',
          width: '95%',
          maxHeight: '250px',
          height: '250px',
          margin: 'auto',
          marginTop: 0,
          marginBottom: 0,
        }}
      >
        {profesionalInfo &&
          profesionalInfo.perfil.profesional_foto_url && (
            <img
              src={profesionalInfo.perfil.profesional_foto_url}
              alt={
                profesionalInfo.perfil?.profesional_nombre_plataforma ||
                "Profesional"
              }
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '8px',
                margin: '0px 0px 0px 0px',
              }}
            />
          )}
      </Box>
      
      {/* Información del profesional */}
      <ProfessionalInfo kinesiologoInfo={profesionalInfo} isMobile={true} shouldShowEvaluaciones={shouldShowEvaluaciones} />
      
      {/* Certificados */}
      {shouldShowCertifications && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 0.25,
              mt: 0.25,
            }}
          >
            <GppGoodIcon sx={{ fontSize: "18px", color: "#3B82F6", mr: 0.625 }} />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                textAlign: "left",
                fontSize: "13px",
                mb: 0,
              }}
            >
              Certificado por Superintendencia de Salud
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 0,
            }}
          >
            <PaidIcon sx={{ fontSize: "18px", color: "#3B82F6", mr: 0.625 }} />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                textAlign: "left",
                mb: 0,
                fontSize: "13px",
              }}
            >
              Reembolsable por ISAPRE y Seguros
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

const renderLegacyDesktop = (profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones) => {
  return (
    <>
      <ProfessionalInfo kinesiologoInfo={profesionalInfo} isMobile={false} shouldShowEvaluaciones={shouldShowEvaluaciones} />
      {profesionalInfo &&
        profesionalInfo.perfil.profesional_foto_url && (
          <Box
            sx={{
              position: 'relative',
              width: '95%',
              maxHeight: '400px',
              height: '400px',
              margin: 'auto',
              marginTop: 1,
            }}
          >
            <img
              src={profesionalInfo.perfil.profesional_foto_url}
              alt={
                profesionalInfo.perfil?.profesional_nombre_plataforma ||
                "Profesional"
              }
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '8px',
              }}
            />
          </Box>
        )}
      {shouldShowCertifications && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
              mt: 1,
            }}
          >
            <GppGoodIcon sx={{ fontSize: "22px", color: "#3B82F6", mr: 0.625 }} />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "14px", xs: "12px" },
                textAlign: "center",
                mb: 0,
              }}
            >
              Profesional certificado por Superintendencia de Salud
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <PaidIcon sx={{ fontSize: "22px", color: "#3B82F6", mr: 0.625 }} />
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "14px", xs: "12px" },
                mb: 0,
              }}
            >
              Las atenciones de este profesional son reembolsables por ISAPRE y Seguros
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

// ==============================================
// COMPONENTE PRINCIPAL DE LA SECCIÓN
// ==============================================

function SeccionPerfil({ profesionalInfo, isMobile, shouldShowEvaluaciones }) {
  // Determinación del escenario
  const scenario = determineScenario(profesionalInfo, isMobile);
  
  // Determinar si se deben mostrar las certificaciones
  const shouldShowCertifications = profesionalInfo?.perfil?.id_profesional_fantasma !== "deeff426-ac81-4611-9dfd-80948d0504fd";

  // Switch de renderizado según escenario
  const renderScenario = () => {
    switch (scenario) {
      case 'newMobileProfileCover':
        return renderNewMobileProfileCover(profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones);
      
      case 'newMobileProfileOnly':
        return renderNewMobileProfileOnly(profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones);
      
      case 'newDesktopProfileCover':
        return renderNewDesktopProfileCover(profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones);
      
      case 'newDesktopProfileOnly':
        return renderNewDesktopProfileOnly(profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones);
      
      case 'legacyMobile':
        return renderLegacyMobile(profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones);
      
      case 'legacyDesktop':
        return renderLegacyDesktop(profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones);
      
      default:
        return renderLegacyDesktop(profesionalInfo, shouldShowCertifications, shouldShowEvaluaciones);
    }
  };

  return (
    <>
      {/* 1. FOTO + INFORMACIÓN DEL PROFESIONAL */}
      {renderScenario()}
    </>
  );
}

export default SeccionPerfil;
